import React, { useState } from 'react';
import { useOnboardingWizard } from '@/context/OnboardingWizardContext';
import { StepIndicator } from './StepIndicator';
import { LEDIndicator } from './LEDIndicator';
import { ThemeToggle } from '@/components/ui/theme-toggle';
import { cn } from '@/lib/utils';
import { ChevronDown } from 'lucide-react';

interface OnboardingMobileStripProps {
  steps: string[];
  className?: string;
}

export const OnboardingMobileStrip: React.FC<OnboardingMobileStripProps> = ({ steps, className }) => {
  const { currentStep } = useOnboardingWizard();
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div className={cn('md:hidden', className)}>
      {/* Strip bar — z-50 so it stays clickable above the backdrop overlay */}
      <div
        className={cn(
          'w-full h-12 flex items-center justify-between px-4 relative z-50',
          'bg-surface-0 border-b border-border',
        )}
      >
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="flex items-center gap-2.5 bg-transparent border-none p-0 cursor-pointer"
        >
          <LEDIndicator state="active" />
          <span className="font-heading text-sm font-semibold uppercase tracking-[0.08em] text-foreground">
            {steps[currentStep]}
          </span>
        </button>
        <div className="flex items-center gap-1">
          <ThemeToggle />
          <button
            type="button"
            onClick={() => setIsExpanded(!isExpanded)}
            className="flex items-center gap-1 bg-transparent border-none p-0 cursor-pointer"
          >
            <span className="font-mono text-xs text-muted-foreground">
              {currentStep + 1}/{steps.length}
            </span>
            <ChevronDown
              className={cn(
                'w-4 h-4 text-muted-foreground transition-transform duration-200',
                isExpanded && 'rotate-180',
              )}
            />
          </button>
        </div>
      </div>

      {/* Expanded overlay */}
      {isExpanded && (
        <>
          {/* Backdrop — closes dropdown on tap */}
          <div
            className="fixed inset-0 bg-black/40 z-40"
            onClick={() => setIsExpanded(false)}
          />
          {/* Step list */}
          <div className="absolute left-0 right-0 z-50 bg-surface-0 border-b border-border shadow-lg">
            <StepIndicator
              steps={steps}
              variant="sidebar"
              className="py-2"
              onStepClick={() => setIsExpanded(false)}
            />
          </div>
        </>
      )}
    </div>
  );
};
